// src/pages/InventoryStockSheet.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../services/firebase";

const DEFAULT_LOCATION_ID = "boutique";

const typeLabel = (t) => {
  switch (t) {
    case "adjust":
      return "Ajustement";
    case "purchase":
    case "reception":
      return "Réception";
    case "sale":
      return "Vente";
    default:
      return t || "—";
  }
};

const formatDateTime = (ts) => {
  if (!ts) return "—";
  try {
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleString("fr-FR");
  } catch {
    return "—";
  }
};

export default function InventoryStockSheet() {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const [locationId] = useState(DEFAULT_LOCATION_ID);
  const [item, setItem] = useState(null);
  const [currentQty, setCurrentQty] = useState(null);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoves, setLoadingMoves] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!itemId) return;

    (async () => {
      setLoading(true);
      setLoadingMoves(true);
      setError("");

      try {
        // 1) Charger l’article + la quantité actuelle en stock
        const itemRef = doc(db, "itemsEK", itemId);
        const stockRef = doc(db, "stock", `${locationId}__${itemId}`);

        const [itemSnap, stockSnap] = await Promise.all([
          getDoc(itemRef),
          getDoc(stockRef),
        ]);

        if (!itemSnap.exists()) {
          setError("Article introuvable.");
          setLoading(false);
          setLoadingMoves(false);
          return;
        }

        const data = itemSnap.data() || {};
        setItem({
          id: itemId,
          name: data.name || data.title || "",
          barcode: data.barcode || "",
          price: data.price || 0,
          cost: data.cost || 0,
        });

        const baseQty = (stockSnap.exists() ? stockSnap.data().qty : 0) || 0;
        setCurrentQty(baseQty);

        // 2) Charger les mouvements, filtrer sur l’article + l’emplacement
        const qMoves = query(
          collection(db, "inventoryMoves"),
          orderBy("at", "desc"),
          limit(500)
        );
        const snap = await getDocs(qMoves);
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const filtered = raw.filter((m) => {
          const sameItem =
            m.itemId === itemId ||
            (m.itemId &&
              typeof m.itemId === "object" &&
              m.itemId.id === itemId) ||
            (m.itemRef && m.itemRef.id === itemId) ||
            (m.item && m.item.id === itemId);

          const sameLocation =
            !m.locationId || m.locationId === locationId;

          return sameItem && sameLocation;
        });

        if (!filtered.length) {
          setMoves([]);
          setLoading(false);
          setLoadingMoves(false);
          return;
        }

        // filtered est déjà trié du plus récent au plus ancien (orderBy desc)
        const sortedDesc = filtered;

        // 3) Reconstituer "Stock après" en partant du stock actuel
        let running = baseQty;
        const processed = sortedDesc.map((m) => {
          let stockAfter = running;
          let qtyDisplay = null;

          if (m.type === "adjust" && typeof m.countedQty === "number") {
            // Inventaire : le stock devient countedQty
            stockAfter = m.countedQty;
            qtyDisplay = m.countedQty;        // quantité affichée = quantité comptée
            running = m.countedQty;           // base pour les mouvements plus anciens
          } else if (typeof m.delta === "number") {
            // Réception / vente : delta positif ou négatif
            stockAfter = running;
            qtyDisplay = Math.abs(m.delta);   // ⬅️ SANS signe +/−
            running = running - m.delta;      // on recule dans le temps
          } else {
            // Mouvement sans info exploitable
            stockAfter = running;
            qtyDisplay = null;
          }

          return {
            ...m,
            _qtyDisplay: qtyDisplay,
            _stockAfter: stockAfter,
          };
        });

        setMoves(processed);
      } catch (e) {
        console.error(e);
        setError("Erreur lors du chargement de la fiche de stock.");
      } finally {
        setLoading(false);
        setLoadingMoves(false);
      }
    })();
  }, [itemId, locationId]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Fiche de stock</h3>
          <p className="text-muted mb-0">
            Article ID : <code>{itemId}</code> · Emplacement suivi :{" "}
            <code>{locationId}</code>
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link
            to={`/admin/inventory/item/${itemId}/edit`}
            className="btn btn-outline-primary"
          >
            Éditer l’article
          </Link>

          <Link to="/admin/inventory" className="btn btn-outline-secondary">
            ← Retour à l’inventaire
          </Link>
        </div>
      </div>

      {/* Infos article */}
      <div className="card mb-3">
        <div className="card-body">
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-3">
              <span className="spinner-border me-2" />
              <span>Chargement…</span>
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-0">{error}</div>
          ) : (
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{item?.name || "Article"}</h5>
                <div className="small text-muted">
                  CB : <code>{item?.barcode || "—"}</code> · Prix :{" "}
                  {item?.price?.toLocaleString?.() || 0} FCFA · Coût :{" "}
                  {item?.cost?.toLocaleString?.() || 0} FCFA
                </div>
              </div>
              <div className="text-end">
                <div className="small text-muted">
                  Quantité actuelle ({locationId})
                </div>
                <div className="fs-4 fw-semibold">
                  {currentQty === null
                    ? "—"
                    : Number(currentQty).toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mouvements */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            Mouvements de stock (nom, type, quantité, stock après)
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(0)}
          >
            Rafraîchir
          </button>
        </div>
        <div className="card-body p-0">
          {loadingMoves ? (
            <div className="d-flex align-items-center justify-content-center py-3">
              <span className="spinner-border me-2" />
              <span>Chargement des mouvements…</span>
            </div>
          ) : moves.length === 0 ? (
            <p className="text-muted text-center py-3 mb-0">
              Aucun mouvement trouvé pour cet article.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Article</th>
                    <th>Mouvement</th>
                    <th className="text-end">Quantité</th>
                    <th className="text-end">Stock après</th>
                    <th>Note / Référence</th>
                  </tr>
                </thead>
                <tbody>
                  {moves.map((m) => (
                    <tr key={m.id}>
                      <td>{formatDateTime(m.at)}</td>
                      <td>{item?.name || "Article"}</td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {typeLabel(m.type)}
                        </span>
                      </td>
                      <td className="text-end">
                        {typeof m._qtyDisplay === "number"
                          ? m._qtyDisplay.toLocaleString()
                          : "—"}
                      </td>
                      <td className="text-end">
                        {typeof m._stockAfter === "number"
                          ? m._stockAfter.toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        <small className="text-muted">
                          {m.note || m.ref || "—"}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
